import { motion } from "framer-motion";
import { ArrowDown, TrendingUp, Users, MessageSquare } from "lucide-react";

export default function LandingHero() {
  return (
    <section
      className="relative w-full overflow-hidden bg-black pb-10 pt-32 font-light text-text-primary antialiased md:pb-16 md:pt-20 border-b border-dark-border"
      style={{
        background:
          "radial-gradient(ellipse at 50% -10%, rgba(255,188,6,0.08) 0%, rgba(0,0,0,1) 45%), #000000",
      }}
    >
      <div
        className="absolute right-0 top-0 h-1/2 w-1/2 pointer-events-none"
        style={{
          background:
            "radial-gradient(circle at 70% 30%, rgba(255, 188, 6, 0.14) 0%, rgba(0, 0, 0, 0) 62%)",
        }}
      />
      <div
        className="absolute left-0 top-0 h-1/2 w-1/2 -scale-x-100 pointer-events-none"
        style={{
          background:
            "radial-gradient(circle at 70% 30%, rgba(255, 188, 6, 0.14) 0%, rgba(0, 0, 0, 0) 62%)",
        }}
      />
 
      <div className="container relative z-10 mx-auto max-w-2xl px-4 text-center md:max-w-4xl md:px-6 lg:max-w-7xl pt-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
        >
          <span className="mb-6 inline-block rounded-full border border-golden-accent/30 bg-golden-accent/5 px-3 py-1 text-xs font-medium tracking-wide text-golden-accent uppercase">
            NEXT GENERATION CREATOR CRM
          </span>
          <h1 className="mx-auto mb-6 max-w-4xl text-4xl font-light tracking-tight text-white md:text-5xl lg:text-7xl leading-[1.08]">
            Monetize Smarter with <br />
            <span className="text-golden-accent">AI-Powered</span> Community Insights
          </h1>
          <p className="mx-auto mb-10 max-w-2xl text-base font-light text-text-secondary md:text-lg">
            SuBs combines intelligent automation with creator-first tooling so you can
            collect payments, manage access, and grow recurring revenue with precision.
          </p>
 
          <div className="mb-10 flex flex-col items-center justify-center gap-4 sm:mb-0 sm:flex-row">
            <a
              href="#how-it-works"
              className="inline-flex items-center justify-center gap-2 rounded-full border border-dark-border/80 bg-dark-secondary/70 px-6 py-3 text-text-secondary transition-all hover:border-golden-accent/40 hover:text-golden-accent sm:w-auto"
            >
              <span>Learn how it works</span>
              <ArrowDown size={16} />
            </a>
          </div>
        </motion.div>
        
        <motion.div
          className="relative mt-2 md:mt-3"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, ease: "easeOut", delay: 0.3 }}
        >
          <div className="relative left-1/2 z-10 flex h-[290px] w-screen -translate-x-1/2 justify-center overflow-hidden md:h-[390px]">
            <div className="pointer-events-none absolute left-1/2 top-0 h-24 w-[78%] -translate-x-1/2 bg-[radial-gradient(circle,rgba(255,188,6,0.26)_0%,rgba(255,188,6,0.08)_50%,rgba(0,0,0,0)_78%)] blur-xl" />
            <img
              src="/globe.png"
              alt="Golden globe horizon"
              className="pointer-events-none absolute left-1/2 top-[-58px] w-[1200px] max-w-none -translate-x-1/2 opacity-100 md:top-[-92px] md:w-[1536px]"
              style={{ filter: "saturate(1.06) contrast(1.08)" }}
            />
          </div>

            <div className="relative z-20 mx-auto max-w-5xl -mt-28 md:-mt-[136px]">
              <div className="relative rounded-xl border border-dark-border bg-dark-secondary/80 backdrop-blur-xl shadow-[0_0_50px_rgba(0,0,0,0.5)] overflow-hidden">
                <div className="h-8 bg-dark-tertiary border-b border-dark-border flex items-center px-4 gap-2">
                    <div className="w-3 h-3 rounded-full bg-red-500/50" />
                    <div className="w-3 h-3 rounded-full bg-yellow-500/50" />
                    <div className="w-3 h-3 rounded-full bg-green-500/50" />
                </div>

                <div className="p-6 md:p-8 grid gap-6 md:grid-cols-3">
                    
                    {/* Stat Card 1 */}
                    <div className="col-span-1 p-4 rounded-lg bg-dark-primary border border-dark-border">
                        <div className="flex items-center gap-3 mb-2">
                             <div className="p-2 rounded bg-golden-accent/10">
                                <TrendingUp className="text-golden-accent h-5 w-5" />
                             </div>
                             <span className="text-sm text-text-secondary">Monthly Revenue</span>
                        </div>
                        <div className="text-2xl font-bold text-white">₹ 4,52,000</div>
                        <div className="text-xs text-functional-success mt-1 flex items-center gap-1">
                            <span>▲ 12.5%</span> <span className="text-text-secondary">vs last month</span>
                        </div>
                    </div>

                    {/* Stat Card 2 */}
                    <div className="col-span-1 p-4 rounded-lg bg-dark-primary border border-dark-border">
                        <div className="flex items-center gap-3 mb-2">
                             <div className="p-2 rounded bg-blue-500/10">
                                <Users className="text-blue-500 h-5 w-5" />
                             </div>
                             <span className="text-sm text-text-secondary">Active Subscribers</span>
                        </div>
                        <div className="text-2xl font-bold text-white">1,247</div>
                         <div className="text-xs text-functional-success mt-1 flex items-center gap-1">
                            <span>▲ 82</span> <span className="text-text-secondary">new today</span>
                        </div>
                    </div>

                    {/* Stat Card 3 */}
                    <div className="col-span-1 p-4 rounded-lg bg-dark-primary border border-dark-border">
                        <div className="flex items-center gap-3 mb-2">
                             <div className="p-2 rounded bg-green-500/10">
                                <MessageSquare className="text-green-500 h-5 w-5" />
                             </div>
                             <span className="text-sm text-text-secondary">Messages Sent</span>
                        </div>
                        <div className="text-2xl font-bold text-white">15,892</div>
                         <div className="text-xs text-text-secondary mt-1 flex items-center gap-1">
                            <span>via WhatsApp API</span>
                        </div>
                    </div>

                    <div className="col-span-3 md:col-span-2 h-48 rounded-lg bg-dark-primary border border-dark-border relative overflow-hidden flex items-end justify-between px-4 pb-0 pt-8 gap-2">
                         {[35, 50, 45, 60, 55, 75, 70, 85, 80, 95, 90, 100].map((val, i) => (
                             <div key={i} className="w-full bg-gradient-to-t from-golden-accent/20 to-golden-accent rounded-t-sm transition-all hover:bg-golden-accent/80" style={{ height: `${val}%`, opacity: 0.6 + (i/20) }}></div>
                         ))}
                         <div className="absolute top-4 left-4 text-xs font-semibold text-text-secondary uppercase tracking-wider">Revenue Trend</div>
                    </div>

                    <div className="col-span-3 md:col-span-1 h-48 rounded-lg bg-dark-primary border border-dark-border p-4 overflow-hidden">
                        <div className="text-xs font-semibold text-text-secondary uppercase tracking-wider mb-3">Live Feed</div>
                        <div className="space-y-3">
                            {[1,2,3].map(i => (
                                <div key={i} className="flex items-center gap-3 text-sm">
                                    <div className="w-2 h-2 rounded-full bg-functional-success animate-pulse" />
                                    <span className="text-white truncated">New subscription via UPI</span>
                                    <span className="ml-auto text-text-secondary text-xs">2m ago</span>
                                </div>
                            ))}
                             <div className="flex items-center gap-3 text-sm opacity-60">
                                    <div className="w-2 h-2 rounded-full bg-blue-500" />
                               <span className="text-white truncated">Slack Channel Access Granted</span>
                                    <span className="ml-auto text-text-secondary text-xs">5m ago</span>
                                </div>
                        </div>
                    </div>

                </div>
              </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
